import React, { useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import {
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseMember,
  EnterpriseMemberInvitationStatus,
} from 'models/Enterprises';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORT_COLS,
  TABLE_COLUMNS,
  EnterpriseTeamMembersTable,
  RWC_TABLE_COLUMNS,
} from './components/TeamMembersTableCells/data';
import { Checkbox } from 'ncoded-component-library';
import { BODY_CONTENT_GENERATE } from './components/TeamMembersTableCells';
import { RightClickDropdown } from 'components/RightClickDropdown';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import useTeamFilterOptions from '../../hooks/useTeamFilterOptions';
import { ColumnSizing } from 'components/ReorderResizeTable/types';

import './TeamMembersTable.styles.scss';

const NOT_ACCEPTED = [
  ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT,
  ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED,
] as EnterpriseMemberInvitationStatus[];

const COLSPAN: Record<string, number> = {
  totalDeals: 6,
  totalDataRooms: 3,
};

type TeamMembersTableProps = {
  className?: string;
  isRWC?: boolean;
  columns: Array<keyof EnterpriseTeamMembersTable>;
  columnSizing: ColumnSizing;
  onReorder: (columns: Array<keyof EnterpriseTeamMembersTable>) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const TeamMembersTable: React.FC<TeamMembersTableProps> = (props) => {
  const {
    className,
    isRWC = false,
    columns,
    columnSizing,
    onReorder,
    onColumnSizingChange,
  } = props;

  const classes = classNames('team-members-table', className);

  const {
    members,
    selected,
    totalPages,
    totalItems,
    isLoading,
    isAllSelected,
    getDropdownOptions,
    onSelectAll,
    checkIfMemberIsSelected,
    updateSelected,
  } = useContext(EnterpriseMembersContext);

  const { columnFilterOptions } = useTeamFilterOptions();

  const headerColTransformation = useMemo(
    () => ({
      checkbox: () => (
        <Checkbox checked={isAllSelected} onChange={onSelectAll} />
      ),
    }),
    [isAllSelected, onSelectAll],
  );

  const defaultColumns = useMemo(
    () => (isRWC ? RWC_TABLE_COLUMNS : TABLE_COLUMNS),
    [isRWC],
  );

  const getDropdownOptionsRef = useRef(getDropdownOptions);
  getDropdownOptionsRef.current = getDropdownOptions;

  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const renderedContent = useMemo(
    () =>
      defaultColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: EnterpriseMember) => (
            <RightClickDropdown
              options={getDropdownOptionsRef.current(
                selectedRef.current?.length ? selectedRef.current : [member],
              )}
            >
              {BODY_CONTENT_GENERATE[col]({
                member,
              })}
            </RightClickDropdown>
          ),
        }),
        {},
      ),
    [defaultColumns],
  );

  return (
    <ReorderResizeTable
      className={classes}
      columns={columns}
      data={members as unknown as EnterpriseTeamMembersTable[]}
      isDataLoading={isLoading}
      totalPages={totalPages}
      totalItems={totalItems}
      recordName="member"
      customSizes={CUSTOM_COL_SIZES}
      headingTranslations={HEADING_TRANSLATIONS}
      sortingValues={SORT_COLS}
      columnFilterOptions={columnFilterOptions}
      tableHeaderContentTransformations={headerColTransformation}
      tableContentTransformations={renderedContent}
      getIsRowSelected={(member) =>
        checkIfMemberIsSelected(member as unknown as EnterpriseMember)
      }
      onRowClick={(member) =>
        updateSelected(member as unknown as EnterpriseMember)
      }
      columnSizing={columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      onReorder={onReorder}
      getColSpan={({ invitationStatus }, col) =>
        NOT_ACCEPTED.includes(invitationStatus) && COLSPAN[col]
      }
    />
  );
};

export default TeamMembersTable;
